import { useCallback, useEffect, useState } from "react";
import {
    cancelInterpretation,
    confirmInterpretation,
} from "../../../api/interpretation";
import { getSmoothingRate } from "../../../api/smoothing";
import { updateWasserman9Graph } from "../../../api/wasserman9";
import GraphsGridPageLayout from "../../../components/interpretation/GraphsGrid/GraphsGridPageLayout";
import { GraphsProvider } from "../../../context/GraphsProvider";
import { useInterpretationContext } from "../../../context/InterpretationContext";
import usePrompt from "../../../hooks/usePrompt";
import useTestId from "../../../hooks/useTestId";
import useWasserman9Data from "../../../hooks/useWasserman9Data";
import { GraphSystems, TestGraph } from "../../../types";
import useTestData from "../../../hooks/useTestData";
import _ from "lodash";
import { clearGraphsCache } from "../../../hooks/useGraphsFactory";

export default function Wasserman9() {
    const { mutate: mutateTestData } = useTestData();
    const [systemIdToShow, setSystemIdToShow] = useState<
        GraphSystems | undefined
    >();
    const [historicTestsToShow, setHistoricTestsToShow] = useState<number[]>(
        []
    );
    const [smoothingValue, setSmoothingValue] = useState(50);
    const [isEnabled, setIsEnabled] = useState(false);

    const { isInterpretationDirty } = useInterpretationContext();
    const testId = useTestId();

    const graphValues = useWasserman9Data({
        testId,
        systemId: systemIdToShow,
        enabled: isEnabled,
    });

    const {
        setOriginalGraphsValue,
        mutate,
        processChangesForCancel,
        getGlobalUnsavedData,
    } = graphValues;
    const globalUnsavedData = getGlobalUnsavedData();
    const thereIsUnsavedData = !_.isEmpty(globalUnsavedData);

    const { nextPathname } = usePrompt({
        isDirty: thereIsUnsavedData,
        isInterpretationDirty,
        onConfirm: () => createConfirmFunction("confirm")(),
        onCancel: () => createConfirmFunction("cancel")(),
    });

    const createConfirmFunction = useCallback(
        (action: "confirm" | "cancel") => {
            return async () => {
                const dataToProcess =
                    action === "cancel"
                        ? processChangesForCancel(globalUnsavedData)
                        : globalUnsavedData;

                const promises: Promise<TestGraph>[] = [];

                dataToProcess?.forEach((item) => {
                    const { ...payload } = item;
                    const payloadData = { ...payload };
                    if (_.isEmpty(payloadData.changes)) {
                        return;
                    }
                    promises.push(
                        updateWasserman9Graph({
                            testId,
                            graph_id: payloadData.graph_id,
                            ...payloadData.changes,
                        })
                    );
                });
                await Promise.all(promises);
                const newGraphs = await mutate();
                setOriginalGraphsValue(newGraphs || null);

                if (
                    isInterpretationDirty &&
                    !nextPathname.includes("interpretation")
                ) {
                    if (action === "cancel") {
                        await cancelInterpretation(testId);
                    } else if (action === "confirm") {
                        await confirmInterpretation(testId);
                    }
                }
                await clearGraphsCache();
                mutateTestData();
            };
        },
        [nextPathname]
    );

    useEffect(() => {
        if (testId) {
            getSmoothingRate(testId).then((data) => {
                setSmoothingValue(data.wasserman9);
                setIsEnabled(true);
            });
        }
    }, [testId]);

    return (
        <GraphsProvider contextValue={graphValues}>
            <GraphsGridPageLayout
                title="Wasserman 9 Panel"
                systemIdToShow={systemIdToShow}
                setSystemIdToShow={setSystemIdToShow}
                historicTestsToShow={historicTestsToShow}
                setHistoricTestsToShow={setHistoricTestsToShow}
                smoothingValue={smoothingValue}
                setSmoothingValue={setSmoothingValue}
            />
        </GraphsProvider>
    );
}
